import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Calendar, TrendingUp, UserCheck, UserX, Clock } from "lucide-react";

interface EmployeeLifecycleData {
  employment_status: string;
  termination_date?: string;
  termination_reason?: string;
  onboarding_completed: boolean;
  offboarding_completed: boolean;
  onboarding_notes?: string;
  offboarding_notes?: string;
  last_promotion_date?: string;
  next_review_date?: string;
  probation_end_date?: string;
  contract_expiry_date?: string;
}

interface EmployeeLifecycleSectionProps {
  data: EmployeeLifecycleData;
  onChange: (field: keyof EmployeeLifecycleData, value: any) => void;
}

const EMPLOYMENT_STATUSES = [
  { value: "active", label: "نشط", icon: UserCheck, color: "bg-green-500" },
  { value: "on_leave", label: "في إجازة", icon: Clock, color: "bg-blue-500" },
  { value: "suspended", label: "موقوف", icon: UserX, color: "bg-yellow-500" },
  { value: "terminated", label: "منتهي الخدمة", icon: UserX, color: "bg-red-500" }
];

export const EmployeeLifecycleSection = ({ data, onChange }: EmployeeLifecycleSectionProps) => {
  const currentStatus = EMPLOYMENT_STATUSES.find(s => s.value === data.employment_status);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">دورة حياة الموظف</h3>
        <p className="text-sm text-muted-foreground">
          إدارة حالة الموظف والتواريخ المهمة
        </p>
      </div>

      {/* حالة التوظيف */}
      <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${currentStatus?.color}`}></div>
          <Label className="text-base font-semibold">حالة التوظيف</Label>
        </div>
        
        <Select
          value={data.employment_status}
          onValueChange={(value) => onChange('employment_status', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EMPLOYMENT_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                <div className="flex items-center gap-2">
                  <status.icon className="w-4 h-4" />
                  {status.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* التأهيل والخروج */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3 p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <Label className="font-semibold">حالة التأهيل (Onboarding)</Label>
            {data.onboarding_completed && (
              <Badge variant="default" className="bg-green-500">
                <UserCheck className="w-3 h-3 ml-1" />
                مكتمل
              </Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-2 space-x-reverse">
            <input
              type="checkbox"
              id="onboarding_completed"
              checked={data.onboarding_completed}
              onChange={(e) => onChange('onboarding_completed', e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="onboarding_completed" className="cursor-pointer">
              تم إكمال عملية التأهيل
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onboarding_notes" className="text-sm">ملاحظات التأهيل</Label>
            <Textarea
              id="onboarding_notes"
              value={data.onboarding_notes || ""}
              onChange={(e) => onChange('onboarding_notes', e.target.value)}
              placeholder="أي ملاحظات حول عملية التأهيل..."
              rows={3}
            />
          </div>
        </div>

        <div className="space-y-3 p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <Label className="font-semibold">حالة الخروج (Offboarding)</Label>
            {data.offboarding_completed && (
              <Badge variant="default" className="bg-orange-500">
                <UserX className="w-3 h-3 ml-1" />
                مكتمل
              </Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-2 space-x-reverse">
            <input
              type="checkbox"
              id="offboarding_completed"
              checked={data.offboarding_completed}
              onChange={(e) => onChange('offboarding_completed', e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="offboarding_completed" className="cursor-pointer">
              تم إكمال عملية الخروج
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="offboarding_notes" className="text-sm">ملاحظات الخروج</Label>
            <Textarea
              id="offboarding_notes"
              value={data.offboarding_notes || ""}
              onChange={(e) => onChange('offboarding_notes', e.target.value)}
              placeholder="أي ملاحظات حول عملية الخروج..."
              rows={3}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* تفاصيل الإنهاء (إذا كان منتهي الخدمة) */}
      {data.employment_status === 'terminated' && (
        <div className="space-y-4 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg">
          <Label className="text-base font-semibold text-red-700 dark:text-red-400">
            تفاصيل إنهاء الخدمة
          </Label>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="termination_date">
                <Calendar className="w-4 h-4 inline ml-1" />
                تاريخ الإنهاء
              </Label>
              <Input
                id="termination_date"
                type="date"
                value={data.termination_date || ''}
                onChange={(e) => onChange('termination_date', e.target.value)}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="termination_reason">سبب الإنهاء</Label>
              <Textarea
                id="termination_reason"
                value={data.termination_reason || ""}
                onChange={(e) => onChange('termination_reason', e.target.value)}
                placeholder="اذكر سبب إنهاء الخدمة..."
                rows={3}
              />
            </div>
          </div>
        </div>
      )}

      <Separator />

      {/* التواريخ المهمة */}
      <div className="space-y-4">
        <Label className="text-base font-semibold">التواريخ المهمة</Label>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="last_promotion_date">
              <TrendingUp className="w-4 h-4 inline ml-1" />
              آخر ترقية
            </Label>
            <Input
              id="last_promotion_date"
              type="date"
              value={data.last_promotion_date || ''}
              onChange={(e) => onChange('last_promotion_date', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="next_review_date">
              <Calendar className="w-4 h-4 inline ml-1" />
              التقييم القادم
            </Label>
            <Input
              id="next_review_date"
              type="date"
              value={data.next_review_date || ''}
              onChange={(e) => onChange('next_review_date', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="probation_end_date">
              <Clock className="w-4 h-4 inline ml-1" />
              نهاية فترة التجربة
            </Label>
            <Input
              id="probation_end_date"
              type="date"
              value={data.probation_end_date || ''}
              onChange={(e) => onChange('probation_end_date', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contract_expiry_date">
              <Calendar className="w-4 h-4 inline ml-1" />
              انتهاء العقد
            </Label>
            <Input
              id="contract_expiry_date"
              type="date"
              value={data.contract_expiry_date || ''}
              onChange={(e) => onChange('contract_expiry_date', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
